import JSBI from 'jsbi';
import Decimal from 'decimal.js';
import { Transaction } from '@near-wallet-selector/core';
import { Provider } from 'near-api-js/lib/providers';
import { WRAPPED_NEAR_ID, TONIC_ID, MEMO, DEFAULT_GAS } from '../../constants';
import { callFunction } from '../../utils/rpc';
import {
  QuoteParams,
  Quote,
  SwapParams,
  CreateSwapRouteInstructionsParams,
} from '../common/types';
import { Amm, SwapMode } from '../types';
import { TonicMarketResponse, TonicOrderBookResponse } from './types';
import { TONIC_FEE_DIVISOR } from './config';
import { buildWithdrawNearTransaction, Percentage } from '../../utils';

const roundForStep = (n: Decimal, step: number | string, up = false) => {
  const s = JSBI.BigInt(step);

  if (up) {
    return n.div(step).ceil().mul(step);
  }
  return n.div(step).floor().mul(step);
};

export class TonicFoundationMarket implements Amm {
  label = 'Tonic';
  id: string;
  contractId = TONIC_ID;
  instanceId: number;
  reserveTokenMints: string[];
  private orderBook: TonicOrderBookResponse;

  constructor(private market: TonicMarketResponse) {
    this.id = this.market.id;
    this.instanceId = Math.round(Math.random() * 1_000_000); // Tonic doesn't have an instance id
    this.orderBook = this.market.orderbook;

    this.reserveTokenMints = [
      this.market.base_token.token_type.type === 'ft'
        ? this.market.base_token.token_type.account_id
        : WRAPPED_NEAR_ID,
      this.market.quote_token.token_type.type === 'ft'
        ? this.market.quote_token.token_type.account_id
        : WRAPPED_NEAR_ID,
    ];
  }

  async getPromiseForUpdate({
    provider,
  }: {
    provider: Provider;
  }): Promise<void> {
    const orderBook = await callFunction<TonicOrderBookResponse>({
      accountId: TONIC_ID,
      method: 'get_orderbook',
      args: {
        market_id: this.id,
        depth: 128,
      },
      provider,
    });
    this.orderBook = orderBook;
  }

  private resolveQuote(params: {
    amountIn: Decimal;
    amountOut: Decimal;
    fee: Decimal;
    stepSize: Decimal;
    slippage: Percentage;
    outputMint: string;
    notEnoughLiquidity: boolean;
    isSellBase: boolean;
    swapMode: SwapMode;
  }): Quote {
    const {
      amountOut,
      amountIn,
      fee,
      slippage,
      stepSize,
      outputMint,
      notEnoughLiquidity,
      swapMode,
      isSellBase,
    } = params;
    const feeAmount = amountOut.mul(fee);
    const outputWithoutFees = amountOut.sub(feeAmount).floor().toString();

    const outputWithoutFeesBI = JSBI.BigInt(outputWithoutFees);
    const amountInRounded =
      swapMode === SwapMode.ExactIn ? amountIn.floor() : amountIn.ceil();
    const inputResult = isSellBase
      ? roundForStep(
          amountInRounded,
          stepSize.toString(),
          swapMode === SwapMode.ExactOut
        )
      : amountInRounded;

    const outputResult = isSellBase
      ? new Decimal(outputWithoutFeesBI.toString())
      : roundForStep(
          new Decimal(outputWithoutFeesBI.toString()),
          stepSize.toString()
        );

    const minOutputResult = isSellBase
      ? new Decimal(slippage.getFor(outputWithoutFeesBI).toString())
      : roundForStep(
          new Decimal(slippage.getFor(outputWithoutFeesBI).toString()),
          stepSize.toString()
        );

    return {
      amountIn: JSBI.BigInt(inputResult.floor().toString()),
      amountOut: JSBI.BigInt(outputResult.floor().toString()),
      minAmountOut: JSBI.BigInt(minOutputResult.floor().toString()),
      feeAmount: JSBI.BigInt(feeAmount.floor().toString()),
      feeMint: outputMint, // TODO
      feePct: this.market.taker_fee_base_rate / TONIC_FEE_DIVISOR,
      notEnoughLiquidity,
      priceImpact: 0,
    };
  }

  getQuote(quoteParams: QuoteParams): Quote {
    if (!this.orderBook) {
      throw new Error(`Order book for market ${this.market.id} is not loaded`);
    }

    const { inputMint, outputMint, amount, swapMode } = quoteParams;

    const isSellBase =
      (this.market.base_token.token_type.type === 'ft' &&
        inputMint === this.market.base_token.token_type.account_id) ||
      (inputMint === WRAPPED_NEAR_ID &&
        this.market.base_token.token_type.type === 'near');

    const side = isSellBase ? this.orderBook.bids : this.orderBook.asks;

    const amountInBase =
      (isSellBase && swapMode === SwapMode.ExactIn) ||
      (!isSellBase && swapMode === SwapMode.ExactOut);

    let restUserAmount = new Decimal(amount.toString());

    const fee = new Decimal(this.market.taker_fee_base_rate).div(
      TONIC_FEE_DIVISOR
    );

    if (quoteParams.swapMode === SwapMode.ExactOut) {
      restUserAmount = restUserAmount.mul(fee.add(1)); // add fee
    }

    let amountIn = new Decimal(0);
    let amountOut = new Decimal(0);

    const baseMultiplier = new Decimal(10).pow(this.market.base_token.decimals);
    const stepSize = new Decimal(this.market.base_token.lot_size);

    for (const order of side) {
      const price = new Decimal(order.limit_price);

      const restAmountInBase = amountInBase
        ? restUserAmount.div(stepSize).floor().mul(stepSize)
        : restUserAmount.mul(baseMultiplier).div(price);

      const orderAmount = new Decimal(order.open_quantity);
      const takeFromLevelInBase = Decimal.min(restAmountInBase, orderAmount);

      const takeFromLevelInQuote = takeFromLevelInBase
        .mul(price)
        .div(baseMultiplier);

      amountIn = isSellBase
        ? amountIn.add(takeFromLevelInBase)
        : amountIn.add(takeFromLevelInQuote);

      amountOut = isSellBase
        ? amountOut.add(takeFromLevelInQuote)
        : amountOut.add(takeFromLevelInBase);

      restUserAmount = restUserAmount.sub(
        amountInBase ? takeFromLevelInBase : takeFromLevelInQuote
      );

      const amountInInBase = isSellBase ? amountIn : amountOut;
      const amountInQuote = isSellBase ? amountOut : amountIn;

      if (takeFromLevelInBase.lessThanOrEqualTo(0)) {
        return this.resolveQuote({
          amountIn,
          amountOut,
          fee,
          stepSize,
          slippage: quoteParams.slippage,
          outputMint: outputMint,
          notEnoughLiquidity:
            !amountInInBase.greaterThanOrEqualTo(
              new Decimal(this.market.base_token.lot_size)
            ) ||
            !amountInQuote.greaterThanOrEqualTo(
              new Decimal(this.market.quote_token.lot_size)
            ),
          swapMode: quoteParams.swapMode,
          isSellBase,
        });
      }
    }

    return this.resolveQuote({
      amountIn,
      amountOut,
      fee,
      stepSize,
      slippage: quoteParams.slippage,
      outputMint: outputMint,
      notEnoughLiquidity: true,
      swapMode: quoteParams.swapMode,
      isSellBase,
    });
  }

  async createSwapInstructions(swapParams: SwapParams): Promise<Transaction[]> {
    const transactions: Transaction[] = [];
    const {
      base_token: {
        token_type: { type: baseType },
      },
      quote_token: {
        token_type: { type: quoteType },
      },
    } = this.market;
    const {
      user,
      swapStep: { amountIn, minAmountOut, inputMint, outputMint },
    } = swapParams;
    const isNativeMarket = baseType === 'near' || quoteType === 'near';
    const isInputInNative = isNativeMarket && inputMint === WRAPPED_NEAR_ID;
    const isOutputInNative = isNativeMarket && outputMint === WRAPPED_NEAR_ID;

    const side =
      (this.market.base_token.token_type.type === 'ft' &&
        inputMint === this.market.base_token.token_type.account_id) ||
      (this.market.base_token.token_type.type === 'near' &&
        inputMint === WRAPPED_NEAR_ID)
        ? 'Sell'
        : 'Buy';

    const swapMessage = {
      action: 'Swap',
      params: [
        {
          type: 'Swap',
          market_id: this.market.id,
          side,
          min_output_token: minAmountOut.toString(),
        },
      ],
    };

    if (isInputInNative) {
      transactions.push(await buildWithdrawNearTransaction(user, amountIn));
    }

    if (isInputInNative) {
      transactions.push({
        receiverId: TONIC_ID,
        signerId: swapParams.user,
        actions: [
          {
            type: 'FunctionCall',
            params: {
              methodName: 'swap_near',
              args: {
                swaps: swapMessage.params,
              },
              gas: DEFAULT_GAS,
              deposit: amountIn.toString(),
            },
          },
        ],
      });
    } else {
      transactions.push({
        receiverId: inputMint,
        signerId: swapParams.user,
        actions: [
          {
            type: 'FunctionCall',
            params: {
              methodName: 'ft_transfer_call',
              args: {
                receiver_id: TONIC_ID,
                amount: amountIn.toString(),
                msg: JSON.stringify(swapMessage),
                memo: MEMO,
              },
              gas: DEFAULT_GAS,
              deposit: '1',
            },
          },
        ],
      });
    }

    if (isOutputInNative) {
      transactions.push({
        receiverId: WRAPPED_NEAR_ID,
        signerId: user,
        actions: [
          {
            type: 'FunctionCall',
            params: {
              methodName: 'near_deposit',
              args: {},
              gas: DEFAULT_GAS,
              deposit: minAmountOut.toString(),
            },
          },
        ],
      });
    }

    return transactions;
  }

  async createSwapRouteInstructions(
    swapParams: CreateSwapRouteInstructionsParams
  ): Promise<Transaction[]> {
    const { provider, user, swapRoute } = swapParams;

    const transactions = [];

    for (let routeStep of swapRoute.steps) {
      const stepTransactions = await routeStep.amm.createSwapInstructions({
        provider,
        user,
        swapStep: routeStep,
      });

      transactions.push(...stepTransactions);
    }

    return transactions;
  }

  static async loadMarkets(params: { provider: Provider }) {
    const { provider } = params;
    const markets = await callFunction<TonicMarketResponse[]>({
      accountId: TONIC_ID,
      method: 'list_markets',
      args: {
        from_index: 0,
        limit: 9999,
      },
      provider,
    });
    return markets
      .filter((market) => market.state === 'Active')
      .map((market) => new TonicFoundationMarket(market));
  }
}
